import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, Validators } from '@angular/forms';
import { TypedFormBuilder, TypedFormGroup } from '@gmrappdev/formsafe';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { AircraftMakeModelHttpClient } from 'src/app/shared/flight-log-api/aircraft-make-models/aircraft-make-models-client.service';
import { TrainingEventHttpClient } from 'src/app/shared/flight-log-api/training-events/training-events-client.service';
import { TrainingProgramHttpClient } from 'src/app/shared/flight-log-api/training-programs/training-programs-client.service';
import { dateTime24HourFormat } from 'src/util/dateLib';
import { SelectItem, toSelectionList } from 'src/util/ui/forms';
import { AircraftMakeModelViewModel } from 'src/view-models/aircraft-make-model-view-model';
import { PrdProcessingState } from 'src/view-models/pilot-view-model';
import { TrainingEventViewModel } from 'src/view-models/training-event-view-model';
import { TrainingProgramViewModel } from 'src/view-models/training-program-view-model';
import {
  PrdDutyPositionType,
  PrdResultType,
  TrainingRecordViewModel,
} from 'src/view-models/training-record-view-model';
import { PrdProcessingStateSelectItems } from '../pilot-form/pilot-form.component';

@Component({
  selector: 'fl-pilot-training-record-form',
  templateUrl: './pilot-training-record-form.component.html',
  styleUrls: ['./pilot-training-record-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PilotTrainingRecordFormComponent implements OnInit, OnDestroy {
  @Input()
  public trainingRecord?: TrainingRecordViewModel;
  @Input()
  public pilotId: string;

  public form: TypedFormGroup<TrainingRecordViewModel>;

  public aircraftMakeModelOptions$: Observable<SelectItem[]>;
  public trainingProgramOptions$: Observable<SelectItem[]>;
  public trainingEventOptions$: Observable<SelectItem[]>;

  public aircraftMakeModelErrors$: Observable<string[]>;
  public trainingProgramErrors$: Observable<string[]>;
  public trainingEventErrors$: Observable<string[]>;

  public aircraftMakeModelsearch$ = new Subject<string>();
  public trainingProgramSearch$ = new Subject<string>();
  public trainingEventSearch$ = new Subject<string>();

  public selectedAircraftMakeModel: SelectItem;
  public selectedtrainingProgram: SelectItem;
  public selectedtrainingEvent: SelectItem;

  public _onDestroy$ = new Subject<void>();

  public processingStateOptions = PrdProcessingStateSelectItems;

  public dutyPositionOptions = Object.values(PrdDutyPositionType);

  public resultOptions = Object.values(PrdResultType);
  public minStartDate = new Date(1950, 0, 1);
  public maxDate = new Date(Date.now());
  public minEndDate: Date;
  public dateTimeFormat = dateTime24HourFormat;

  public isTraingEventOther$: Observable<boolean>;

  constructor(
    private _tfb: TypedFormBuilder,
    private _aircraftMakeModel: AircraftMakeModelHttpClient,
    private _trainingProgram: TrainingProgramHttpClient,
    private _trainingEvent: TrainingEventHttpClient,
  ) {}

  public ngOnInit() {
    const trainingRecord: Partial<TrainingRecordViewModel> =
      this.trainingRecord != null ? this.trainingRecord : {};
    this.form = this._tfb.group<TrainingRecordViewModel>({
      id: [trainingRecord.id],
      pilotId: [this.pilotId],
      aircraftMakeModelId: [trainingRecord.aircraftMakeModelId, Validators.required],
      dutyPosition: [trainingRecord.dutyPosition, Validators.required],
      endDate: [trainingRecord.endDate],
      evaluatorComments: [trainingRecord.evaluatorComments],
      lastProcessedDate: [trainingRecord.lastProcessedDate],
      otherEventDescription: [trainingRecord.otherEventDescription],
      prdSourceSystemId: [trainingRecord.prdSourceSystemId],
      processingError: [trainingRecord.processingError],
      processingState: [trainingRecord.processingState ?? PrdProcessingState.ReadyForTransmission],
      result: [trainingRecord.result, Validators.required],
      startDate: [trainingRecord.startDate, Validators.required],
      suppressed: [trainingRecord.suppressed ?? false],
      trainingEventId: [trainingRecord.trainingEventId, Validators.required],
      trainingProgramId: [trainingRecord.trainingProgramId, Validators.required],
      unsatisfactoryComments: [trainingRecord.unsatisfactoryComments],
      pilot: null as any,
      aircraftMakeModel: null as any,
      trainingEvent: null as any,
      trainingProgram: null as any,
    });

    if (trainingRecord.aircraftMakeModel != null) {
      this.selectedAircraftMakeModel = {
        value: trainingRecord.aircraftMakeModel.id,
        label: trainingRecord.aircraftMakeModel.code,
      };
    }

    if (trainingRecord.trainingProgram != null) {
      this.selectedtrainingProgram = {
        value: trainingRecord.trainingProgram.id,
        label: trainingRecord.trainingProgram.code,
      };
    } else {
      this._trainingProgram
        .getAll({ codeLike: '135' })
        .pipe(take(1))
        .subscribe((data) => {
          const trainingProgram = data.results[0];
          this.selectedtrainingProgram = {
            value: trainingProgram.id,
            label: trainingProgram.code,
          };
          this.form.controls.trainingProgramId.markAsTouched();
        });
    }

    if (trainingRecord.trainingEvent != null) {
      this.selectedtrainingEvent = {
        value: trainingRecord.trainingEvent.id,
        label: `${trainingRecord.trainingEvent.description}`,
      };
    }

    this.aircraftMakeModelOptions$ = this.observeAircraftMakeModelOptions(
      this.aircraftMakeModelsearch$,
    );

    this.trainingProgramOptions$ = this.observeTrainingProgramOptions(this.trainingProgramSearch$);

    this.trainingEventOptions$ = combineLatest([
      this.trainingEventSearch$,
      this.form.controls.trainingProgramId.valueChanges,
    ]).pipe(
      startWith([null, null]),
      switchMap(([search, trainingProgramId]) =>
        this.observeTrainingEventOptions(search, trainingProgramId),
      ),
    );
    this.isTraingEventOther$ = combineLatest([
      this.trainingEventOptions$,
      this.form.controls.trainingEventId.valueChanges,
    ]).pipe(map(([options, values]) => options.find((_) => _.label === 'Other')?.value === values));
    this.aircraftMakeModelErrors$ = this.form.controls.aircraftMakeModelId.statusChanges.pipe(
      map((status) => {
        if (this.form.controls.aircraftMakeModelId.valid) {
          return [];
        } else {
          const errors: string[] = [];
          if (this.form.controls.aircraftMakeModelId.hasError('required')) {
            errors.push('Reason is required');
          }
          return errors;
        }
      }),
    );

    this.form.controls.trainingProgramId.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(() => {
        if (this.form.touched) {
          this.form.controls.trainingEventId.reset();
        }
      });

    this.form.controls.startDate.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((value) => {
        if (this.form.touched) {
          this.minEndDate = value;
        }
      });

    this.form.controls.endDate.valueChanges.pipe(takeUntil(this._onDestroy$)).subscribe(() => {
      if (this.form.touched) {
      }
    });

    if (trainingRecord != null) {
      this.form.controls.trainingEventId.setValue(trainingRecord.trainingEventId);
    }
  }

  public onAircraftMakeModelSearch(searchTerm: string) {
    return this.aircraftMakeModelsearch$.next(searchTerm);
  }

  public onTrainingProgramSearch(searchTerm: string) {
    return this.trainingProgramSearch$.next(searchTerm);
  }

  public onTrainingEventSearch(searchTerm: string) {
    return this.trainingEventSearch$.next(searchTerm);
  }

  public ngOnDestroy() {
    this._onDestroy$.next();
  }

  public getControlRequiredError(control: AbstractControl) {
    if (control.valid) {
      return [];
    } else {
      const errors: string[] = [];
      if (control.hasError('required')) {
        errors.push('Reason is required');
      }
      return errors;
    }
  }

  public observeAircraftMakeModelOptions(searchTerm: Observable<string | null>) {
    return searchTerm
      .pipe(startWith(null as string | null))
      .pipe(
        switchMap((search) =>
          this._aircraftMakeModel.getAll({
            codeLike: search ?? undefined,
            take: 200,
          }),
        ),
      )
      .pipe(map((r) => r.results))
      .pipe(
        map(
          toSelectionList<AircraftMakeModelViewModel>(
            (aircraftMakeModel) => `${aircraftMakeModel.code}`,
            'id',
          ),
        ),
      );
  }

  public observeTrainingProgramOptions(searchTerm: Observable<string | null>) {
    return searchTerm
      .pipe(startWith(null as string | null))
      .pipe(
        switchMap((search) =>
          this._trainingProgram.getAll({
            codeLike: search ?? undefined,
            take: 25,
          }),
        ),
      )
      .pipe(map((r) => r.results?.sort((a, b) => a.code.localeCompare(b.code))))
      .pipe(
        map(
          toSelectionList<TrainingProgramViewModel>(
            (trainingProgram) => `${trainingProgram.code}`,
            'id',
          ),
        ),
      );
  }

  public observeTrainingEventOptions(
    searchTerm: string | null,
    trainingProgramId: string | null | undefined,
  ) {
    const options =
      trainingProgramId != null
        ? this._trainingEvent.getByTrainingProgramId(trainingProgramId).pipe(
            map((data) => data.results?.sort((a, b) => a.description.localeCompare(b.description))),
            map((events) => {
              if (searchTerm != null) {
                return events.filter(
                  (event) =>
                    event.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    event.description.toLowerCase().includes(searchTerm.toLowerCase()),
                );
              } else {
                return events;
              }
            }),
          )
        : of([] as any as TrainingEventViewModel[]);
    return options.pipe(
      map(
        toSelectionList<TrainingEventViewModel>(
          (trainingEvent) => `${trainingEvent.description}`,
          'id',
        ),
      ),
    );
  }
}
